import { LinkOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Checkbox } from 'antd';
import SafeImage from 'components/SafeImage';
import useSmartField from 'hooks/useSmartField';
import { useMemo, HTMLAttributes } from 'react';
import { useTranslate } from '@tolgee/react';
import { ItemLink } from 'views/Conversation/Sider/TradeProduct';
import { FV, getItemsCheckedNext, isItemChecked, isItemSelected } from './share';
import { openExternalURL } from 'utils/openExternalURL';

const Container = styled.div`
  position: relative;
  padding: 10px 20px 10px 8px;
  border: solid 1px #d3d7e0;
  border-radius: 2px;
`;

const ItemWrapper = styled.div`
  display: flex;
  align-items: center;
  > label {
    flex: 0 0 auto;
    margin-right: 8px;
  }
  > div {
    flex: 1 0 0;
  }
`;

const ItemContent = styled.div`
  display: flex;
  cursor: pointer;
  overflow: hidden;
  > div {
    flex: 0 0 auto;
    margin-right: 8px;
  }
  > section {
    flex: 1 0 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    overflow: hidden;
  }
`;

const ItemTitle = styled.p`
  line-height: 20px;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-all;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  > section {
    display: none;
  }
  &:hover {
    color: var(--ant-primary-color);
    > section {
      display: block;
    }
  }
`;

const ItemInfo = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  font-size: 12px;
  color: #84868c;
  > * {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const StoreName = styled.div`
  max-width: 25%;
`;

const Divider = styled.div`
  width: 4.5%;
  text-align: center;
`;

const ID = styled.div`
  max-width: 33%;
`;

const SPU = styled.div`
  max-width: 33%;
`;

const Image = styled(SafeImage)`
  width: 60px;
  height: 60px;
`;

const SkuWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-left: 24px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: solid 1px #eeeeee;
  > * {
    flex: 0 0 50%;
    margin-left: 0 !important;
    overflow: hidden;
    .ant-checkbox {
      flex: 0 0 auto;
    }
    > span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
`;

export interface ItemProps extends HTMLAttributes<HTMLDivElement> {
  item: Aide.Item;
  storeName: string;
}

function Item(props: ItemProps) {
  const { item, storeName, ...otherProps } = props;

  const {
    itemName,
    imageUrl: [imageUrl] = [],
    externalItemId: spuId,
    skuInfo = [],
    externalShopId: externalStoreId,
    itemUrl,
    itemSku,
  } = item;

  const { t } = useTranslate();

  const {
    field: { value, onChange },
  } = useSmartField<FV['selectedItems']>('selectedItems');

  const {
    field: { value: skuEnable = true },
  } = useSmartField<FV['skuEnable']>('skuEnable');

  const spuTargetIndex = useMemo(
    () => value.findIndex((selectedItem) => isItemSelected(selectedItem, item)),
    [value, item],
  );

  const spuTarget = value[spuTargetIndex];

  const spuChecked = useMemo(() => isItemChecked(value, item), [value, item]);

  const spuIndeterminate = spuTarget !== undefined && !spuChecked;

  const checkbox = (
    <Checkbox
      checked={spuChecked}
      indeterminate={spuIndeterminate}
      onChange={({ target: { checked } }) => {
        onChange(getItemsCheckedNext(value, [item], checked));
      }}
    />
  );

  const content = (
    <ItemContent
      onClick={() => {
        onChange(getItemsCheckedNext(value, [item], !spuChecked));
      }}
    >
      <Image hasSmallPic="both" src={imageUrl} width={60} />
      <section>
        <ItemTitle
          title={itemName}
          onClick={(e) => {
            e.stopPropagation();
            openExternalURL(itemUrl, '__blank');
          }}
        >
          {itemName}
          <ItemLink>
            <LinkOutlined />
          </ItemLink>
        </ItemTitle>
        <ItemInfo>
          <StoreName title={storeName}>{storeName}</StoreName>
          <Divider>|</Divider>
          <ID title={spuId}>
            ID:
            {spuId}
          </ID>
          <Divider>|</Divider>
          <SPU title={itemSku}>
            {t('aide.主商品货号')}:{itemSku || '-'}
          </SPU>
        </ItemInfo>
      </section>
    </ItemContent>
  );

  const skuList =
    skuInfo.length === 0 || !skuEnable ? null : (
      <SkuWrapper>
        {skuInfo.map((_) => {
          const { sellerSku, skuId, skuName } = _;

          const { skuChecked, allChecked } = (() => {
            if (spuTarget === undefined) {
              return {
                skuChecked: false,
                allChecked: false,
              };
            }

            const { skuIds = [] } = spuTarget;

            if (skuIds.length === 0) {
              return {
                skuChecked: true,
                allChecked: true,
              };
            }

            return {
              skuChecked: skuIds.includes(skuId),
              allChecked: false,
            };
          })();

          const text = `${sellerSku}(${skuName || '-'})`;

          return skuEnable === 'view' ? (
            <span key={skuId} title={text}>
              {text}
            </span>
          ) : (
            <Checkbox
              key={skuId}
              checked={skuChecked}
              onChange={({ target: { checked } }) => {
                if (checked) {
                  const next = [...value];

                  if (spuTarget) {
                    next[spuTargetIndex] = {
                      ...spuTarget,
                      skuIds: [...spuTarget.skuIds, skuId],
                    };
                  } else {
                    next.push({
                      externalStoreId,
                      spuId,
                      skuIds: [skuId],
                    });
                  }

                  onChange(next);
                } else {
                  const nextSpuIds = allChecked
                    ? skuInfo.map(({ skuId }) => skuId).filter((_) => _ !== skuId)
                    : spuTarget.skuIds.filter((_) => _ !== skuId);

                  const next = [...value];

                  if (nextSpuIds.length === 0) {
                    next.splice(spuTargetIndex, 1);
                  } else {
                    next[spuTargetIndex] = {
                      ...spuTarget,
                      skuIds: nextSpuIds,
                    };
                  }

                  onChange(next);
                }
              }}
            >
              <span title={text}>{text}</span>
            </Checkbox>
          );
        })}
      </SkuWrapper>
    );

  return (
    <Container {...otherProps}>
      <ItemWrapper>
        {checkbox}
        {content}
      </ItemWrapper>
      {skuList}
    </Container>
  );
}

export default Item;
